// stereo-core: 3D vector helpers. Frame: x = East, y = North, z = Up.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
export type Vec3 = [number, number, number];

export const DEG = Math.PI / 180;
export const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export const norm = (a: Vec3) => Math.hypot(a[0], a[1], a[2]);
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const neg = (a: Vec3): Vec3 => [-a[0], -a[1], -a[2]];
export function unit(a: Vec3): Vec3 {
  const n = norm(a);
  if (!(n > 0)) throw new Error("zero-length vector");
  return [a[0] / n, a[1] / n, a[2] / n];
}
/** Axial angle between two lines/poles, degrees in [0, 90]. */
export function axialAngle(a: Vec3, b: Vec3): number {
  const u = unit(a), v = unit(b);
  // atan2 form stays accurate for tiny angles (acos loses ~1e-6 deg there)
  return Math.atan2(norm(cross(u, v)), Math.abs(dot(u, v))) / DEG;
}
/** Two unit vectors perpendicular to `a` and to each other. */
export function perpBasis(a: Vec3): [Vec3, Vec3] {
  const u = unit(a);
  const ref: Vec3 = Math.abs(u[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  const e1 = unit(cross(u, ref));
  const e2 = cross(u, e1);
  return [e1, e2];
}
